import { PaperPlaneIcon } from '@radix-ui/react-icons';
import { Flex } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { DialogButton } from 'components/common/dialogs/button';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonFormGrid } from 'components/common/form/grid';
import { CommonSelectInput } from 'components/common/form/select';
import { CommonTextareaInput } from 'components/common/form/textarea';
import { ICustomRequestMsg } from 'lib_ts/interfaces/machine-msg/i-custom-request';
import { useState } from 'react';
import { AdminMachinesService } from 'services/admin/machines.service';

interface IProps {
  machineID: string;
}

const PRESETS: {
  id: string;
  label: string;
  data: any;
}[] = [];

// if anything needs to be pre-defined, it goes here
const DEFAULT_PAYLOAD: ICustomRequestMsg = {
  targets: ['submodule1', 'submodule2'],
  value: {
    sample_key: 1,
    another_key: 2,
  },
};

export const CustomRequestTab = ({ machineID }: IProps) => {
  const [dataJSON, setDataJSON] = useState<string>(
    JSON.stringify(DEFAULT_PAYLOAD, null, 2)
  );

  const handleSendRequest = () => {
    if (!dataJSON) {
      NotifyHelper.warning({
        message_md: 'Payload value cannot be empty.',
      });
      return;
    }

    try {
      const jsonValue: ICustomRequestMsg = JSON.parse(dataJSON);

      if (typeof jsonValue !== 'object') {
        NotifyHelper.warning({
          message_md: 'Payload value must be a JSON object.',
        });
        return;
      }

      AdminMachinesService.getInstance().postCustomRequest(
        machineID,
        jsonValue
      );
    } catch (error) {
      NotifyHelper.warning({
        message_md: 'Invalid JSON format.',
      });
    }
  };

  return (
    <ErrorBoundary identifier="CustomRequestTab">
      <CommonFormGrid columns={1}>
        <CommonTextareaInput
          id="custom-request-payload"
          label="Payload JSON"
          name="config"
          rows={10}
          value={dataJSON}
          onChange={(v) => setDataJSON(v ?? '{}')}
          hint_md="Some changes will only take effect after the machine is restarted (e.g. connections)."
          monospace
        />
        <CommonSelectInput
          id="custom-request-preset"
          name="preset"
          label="Preset"
          options={PRESETS.map((p) => ({
            label: p.label,
            value: p.id,
          }))}
          onChange={(v) => {
            const preset = PRESETS.find((p) => p.id === v);
            setDataJSON(
              JSON.stringify(preset?.data ?? DEFAULT_PAYLOAD, null, 2)
            );
          }}
          optional
        />
        <Flex justify="end">
          <DialogButton
            icon={<PaperPlaneIcon />}
            label="Send Request"
            onClick={handleSendRequest}
            variant="solid"
          />
        </Flex>
      </CommonFormGrid>
    </ErrorBoundary>
  );
};
